// The Copilot panel's calls (server: server/src/routes/copilot.routes.js).
//
// The chat turn is read as a stream of JSON lines so the person sees what is
// happening while a slow tool runs (the Assessment Designer takes a minute or
// two). It goes through fetch rather than axios because axios cannot read a
// response as it arrives in the browser. Everything else uses the shared
// axios instance, whose interceptor refreshes an expired session.
import api from './api';
import { API_BASE_URL } from '../config/constants';

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function copilotStatus() {
  const res = await api.get('/copilot/status');
  return res.data?.data || {};
}

/**
 * One turn. onEvent receives each event as it arrives:
 *   working | text | card | notice | reply | error | done | ping
 */
export async function streamChat({ message, history, page, onEvent, signal }) {
  const send = () => fetch(`${API_BASE_URL}/copilot/chat`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ message, history, page }),
    signal,
  });

  let res = await send();
  if (res.status === 401) {
    // The shared axios instance refreshes the session on a 401; borrow it.
    await api.get('/copilot/status').catch(() => {});
    res = await send();
  }
  if (!res.ok) {
    let body = {};
    try { body = await res.json(); } catch (e) { body = {}; }
    const err = new Error(body.message || 'The Copilot could not answer just now. Try again in a minute.');
    err.status = res.status;
    throw err;
  }

  const handle = (line) => {
    const t = line.trim();
    if (!t) return;
    try { onEvent(JSON.parse(t)); } catch (e) { /* a broken line is skipped, not fatal */ }
  };

  if (!res.body || !res.body.getReader) {
    (await res.text()).split('\n').forEach(handle);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let i = buffer.indexOf('\n');
    while (i >= 0) {
      handle(buffer.slice(0, i));
      buffer = buffer.slice(i + 1);
      i = buffer.indexOf('\n');
    }
  }
  handle(buffer);
}

export async function confirmCard(actionId, body = {}) {
  try {
    const res = await api.post(`/copilot/actions/${actionId}/confirm`, body);
    return res.data?.data || { ok: true, status: 'done', message: 'Done.' };
  } catch (e) {
    const d = e.response?.data;
    return { ok: false, status: e.response?.status === 422 ? 'failed' : 'refused', message: d?.message || 'That could not be done just now. Nothing was changed.' };
  }
}

export async function cancelCard(actionId) {
  try {
    const res = await api.post(`/copilot/actions/${actionId}/cancel`, {});
    return res.data?.data || { ok: true, status: 'cancelled', message: 'Cancelled.' };
  } catch (e) {
    return { ok: false, status: 'refused', message: e.response?.data?.message || 'Could not cancel just now.' };
  }
}
